import React, { useEffect, useState, useMemo } from "react";
import * as XLSX from "xlsx";
import "./Table.css";

import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  Tooltip, Legend, XAxis, YAxis, CartesianGrid, ResponsiveContainer
} from "recharts";

/* ─────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────── */

const COLORS = [
  "#0088FE", "#00C49F", "#FFBB28", "#FF8042",
  "#845EC2", "#D65DB1", "#2C73D2", "#008F7A",
  "#FFC75F", "#F9F871"
];

/* Distinct header band colour per breakdown group */
const BD_HEADER_COLORS = [
  "#4f46e5", "#0891b2", "#059669", "#d97706", "#dc2626", "#7c3aed"
];

const BREAKDOWN_OPTIONS = [
  { key: "schemeName",        label: "Scheme Name" },
  { key: "serviceType",       label: "Service Type" },
  { key: "clientProjectName", label: "Project Name" },
  { key: "clientProjectType", label: "Project Type (PID)" },
  { key: "tgGender",          label: "Gender" },
  { key: "tgPWD",             label: "PWD" },
  { key: "tgRationCard",      label: "Ration Card" },
  { key: "tgOccupation",      label: "Occupation" },
  { key: "tgAge",             label: "Age Group" },
  { key: "tgIncome",          label: "Income Range" },
  { key: "thematicArea",      label: "Thematic Area" },
];

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */

const Table = ({ filters }) => {

  const [view, setView]           = useState("table");
  const [stateData, setStateData] = useState([]);
  const [loading, setLoading]     = useState(false);

  /*
    allBreakdownData: { [breakdownKey]: rowArray }
    Holds fetched API rows for every active breakdown key simultaneously.
  */
  const [allBreakdownData, setAllBreakdownData] = useState({});
  const [loadingBD, setLoadingBD]               = useState(false);

  /* ─────────────── API SELECTION ─────────────── */

  const hasDateFilter = Boolean(filters.fromMonth) && Boolean(filters.toMonth);

  const STATE_API = hasDateFilter
    ? "https://api-sales-insights.haqdarshak.com/state/month"
    : "https://api-sales-insights.haqdarshak.com/state";

  const BREAKDOWN_API = hasDateFilter
    ? "https://api-sales-insights.haqdarshak.com/state/month/breakdown"
    : "https://api-sales-insights.haqdarshak.com/state/breakdown";

  /* ─────────────── ACTIVE BREAKDOWN FILTERS ─────────────── */

  const activeBreakdownOptions = useMemo(() => {
    return BREAKDOWN_OPTIONS.filter(({ key }) => {
      const val = filters[key];
      return Array.isArray(val) ? val.length > 0 : Boolean(val);
    });
  }, [filters]);

  /* ─────────────── FETCH AGGREGATED DATA ─────────────── */

  useEffect(() => {
    const controller = new AbortController();

    const fetchTableData = async () => {
      try {
        setLoading(true);
        const response = await fetch(STATE_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(filters),
          signal: controller.signal
        });
        if (!response.ok) throw new Error("Failed to fetch");
        const data = await response.json();
        setStateData(Array.isArray(data) ? data : []);
      } catch (error) {
        if (error.name !== "AbortError") console.error(error);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchTableData();
    return () => controller.abort();
  }, [filters, STATE_API]);

  /* ─────────────────────────────────────────────
     FETCH ALL ACTIVE BREAKDOWNS IN PARALLEL
     One request per active breakdown key.
     Results stored as { [key]: rowArray }.
  ───────────────────────────────────────────── */

  useEffect(() => {
    if (activeBreakdownOptions.length === 0) {
      setAllBreakdownData({});
      return;
    }

    const controller = new AbortController();

    const fetchAll = async () => {
      setLoadingBD(true);
      try {
        const results = await Promise.all(
          activeBreakdownOptions.map(({ key }) =>
            fetch(BREAKDOWN_API, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ ...filters, breakdownKey: key }),
              signal: controller.signal
            })
              .then(r => r.ok ? r.json() : [])
              .then(data => ({ key, data: Array.isArray(data) ? data : [] }))
              .catch(() => ({ key, data: [] }))
          )
        );

        if (!controller.signal.aborted) {
          const map = {};
          results.forEach(({ key, data }) => { map[key] = data; });
          setAllBreakdownData(map);
        }
      } catch (err) {
        if (err.name !== "AbortError") console.error(err);
      } finally {
        if (!controller.signal.aborted) setLoadingBD(false);
      }
    };

    fetchAll();
    return () => controller.abort();
  }, [filters, activeBreakdownOptions, BREAKDOWN_API]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─────────────────────────────────────────────
     BUILD COMBINED PIVOT
     pivotByKey[key] = { bdValues: string[], stateMap: { [State]: { [val]: metrics } } }
     allStates = union of every state across all breakdown results
  ───────────────────────────────────────────── */

  const { pivotByKey, allStates } = useMemo(() => {
    if (activeBreakdownOptions.length === 0) {
      return { pivotByKey: {}, allStates: [] };
    }

    const pivotByKey = {};
    const stateSet   = new Set();

    activeBreakdownOptions.forEach(({ key }) => {
      const rows     = allBreakdownData[key] || [];
      const valueSet = new Set();
      const stateMap = {};

      rows.forEach(row => {
        const state = row.State          || "Unknown";
        const val   = row.breakdownValue || "Unknown";
        valueSet.add(val);
        stateSet.add(state);
        if (!stateMap[state]) stateMap[state] = {};
        stateMap[state][val] = {
          opened: Number(row.opened) || 0,
          ds:     Number(row.ds)     || 0,
          br:     Number(row.br)     || 0,
          bv:     Number(row.bv)     || 0,
        };
      });

      pivotByKey[key] = { bdValues: [...valueSet].sort(), stateMap };
    });

    return { pivotByKey, allStates: [...stateSet].sort() };
  }, [activeBreakdownOptions, allBreakdownData]);

  const hasBreakdownData =
    activeBreakdownOptions.length > 0 &&
    activeBreakdownOptions.some(({ key }) => (allBreakdownData[key] || []).length > 0);

  /* ─────────────── CHART PROCESSING ─────────────── */

  const processedData = stateData.map(row => ({
    State:  row.State,
    opened: Number(row.opened) || 0,
    ds:     Number(row.ds)     || 0,
    br:     Number(row.br)     || 0,
    bv:     Number(row.bv)     || 0,
  }));

  const sortedByBV  = [...processedData].sort((a, b) => b.bv - a.bv);
  const topStates   = sortedByBV.slice(0, 10);
  const othersTotal = sortedByBV.slice(10).reduce((s, i) => s + i.bv, 0);
  const pieData     = othersTotal > 0
    ? [...topStates, { State: "Others", bv: othersTotal }]
    : topStates;

  /* ─────────────── EXPORT ─────────────── */

  const exportToExcel = () => {
    let exportRows;

    if (hasBreakdownData) {
      exportRows = allStates.map(state => {
        const row = { State: state };
        activeBreakdownOptions.forEach(({ key, label }) => {
          const { bdValues, stateMap } = pivotByKey[key] || { bdValues: [], stateMap: {} };
          bdValues.forEach(val => {
            const cell = (stateMap[state] || {})[val] || {};
            row[`${label} – ${val} – Apps Opened`]       = cell.opened || 0;
            row[`${label} – ${val} – Docket Submitted`]  = cell.ds     || 0;
            row[`${label} – ${val} – Benefits Received`] = cell.br     || 0;
            row[`${label} – ${val} – Benefit Value`]     = cell.bv     || 0;
          });
        });
        return row;
      });
    } else {
      exportRows = stateData;
    }

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "State Data");
    XLSX.writeFile(workbook, "state_data.xlsx");
  };

  /* ─────────────── HELPERS ─────────────── */

  const fmt    = n => (n || 0).toLocaleString("en-IN");
  const fmtINR = n => `₹ ${(n || 0).toLocaleString("en-IN")}`;

  /* ─────────────── ACTIVE FILTER PILLS ─────────────── */

  const activeFilterLabels = useMemo(() => {
    return BREAKDOWN_OPTIONS
      .filter(({ key }) => {
        const val = filters[key];
        return Array.isArray(val) ? val.length > 0 : Boolean(val);
      })
      .map(({ key, label }) => {
        const val = filters[key];
        return `${label}: ${Array.isArray(val) ? val.join(", ") : val}`;
      });
  }, [filters]);

  /* ─────────────── RENDER ─────────────── */

  return (
    <div className="table-container">

      {/* ── TOP BAR ── */}
      <div className="table-topbar">
        <div className="view-toggle">
          <button
            className={view === "table"  ? "active" : ""}
            onClick={() => setView("table")}
          >Table</button>
          <button
            className={view === "charts" ? "active" : ""}
            onClick={() => setView("charts")}
          >Charts</button>
        </div>
      </div>

      {/* ── ACTIVE FILTER PILLS ── */}
      {activeFilterLabels.length > 0 && (
        <div className="active-filters-row">
          <span className="active-filters-label">Active filters:</span>
          {activeFilterLabels.map((lbl, i) => (
            <span key={i} className="filter-pill">{lbl}</span>
          ))}
        </div>
      )}

      {/* ── CONTENT ── */}
      {(loading || loadingBD) ? (
        <p className="loading-msg">
          {loading ? "Loading table data…" : "Loading breakdown data…"}
        </p>

      ) : view === "table" ? (

        hasBreakdownData ? (

          /* ── COMBINED BREAKDOWN TABLE (all active breakdowns side-by-side) ── */
          <div className="breakdown-table-wrap">
            <table className="breakdown-table">
              <thead>

                {/* ROW 1 — breakdown group labels (e.g. "Scheme Name", "Gender") */}
                <tr>
                  <th rowSpan={3} className="th-state">State</th>
                  {activeBreakdownOptions.map(({ key, label }, gi) => {
                    const { bdValues } = pivotByKey[key] || { bdValues: [] };
                    if (!bdValues.length) return null;
                    return (
                      <th
                        key={key}
                        colSpan={bdValues.length * 4}
                        className="th-group"
                        style={{ background: BD_HEADER_COLORS[gi % BD_HEADER_COLORS.length] }}
                      >
                        {label}
                      </th>
                    );
                  })}
                </tr>

                {/* ROW 2 — individual breakdown values (e.g. "PM Awas", "Male") */}
                <tr>
                  {activeBreakdownOptions.map(({ key }, gi) => {
                    const { bdValues } = pivotByKey[key] || { bdValues: [] };
                    const color = BD_HEADER_COLORS[gi % BD_HEADER_COLORS.length];
                    return bdValues.map(val => (
                      <th
                        key={`${key}-${val}`}
                        colSpan={4}
                        className="th-bdvalue"
                        style={{ background: color, filter: "brightness(1.18)" }}
                      >
                        {val}
                      </th>
                    ));
                  })}
                </tr>

                {/* ROW 3 — metric sub-headers */}
                <tr>
                  {activeBreakdownOptions.map(({ key }) => {
                    const { bdValues } = pivotByKey[key] || { bdValues: [] };
                    return bdValues.map(val => (
                      <React.Fragment key={`${key}-${val}-m`}>
                        <th className="th-metric">Apps Opened</th>
                        <th className="th-metric">Docket Sub.</th>
                        <th className="th-metric">Benefits Rec.</th>
                        <th className="th-metric">Benefit Value</th>
                      </React.Fragment>
                    ));
                  })}
                </tr>

              </thead>
              <tbody>
                {allStates.map((state, i) => (
                  <tr key={i}>
                    <td className="td-state">{state}</td>
                    {activeBreakdownOptions.map(({ key }) => {
                      const { bdValues, stateMap } = pivotByKey[key] || { bdValues: [], stateMap: {} };
                      return bdValues.map(val => {
                        const cell = (stateMap[state] || {})[val] || {};
                        return (
                          <React.Fragment key={`${key}-${val}`}>
                            <td>{fmt(cell.opened)}</td>
                            <td>{fmt(cell.ds)}</td>
                            <td>{fmt(cell.br)}</td>
                            <td>{fmtINR(cell.bv)}</td>
                          </React.Fragment>
                        );
                      });
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        ) : (

          /* ── AGGREGATED TABLE ── */
          <table>
            <thead>
              <tr>
                <th>State</th>
                <th>Applications Opened</th>
                <th>Docket Submitted</th>
                <th>Benefits Received</th>
                <th>Benefit Value</th>
              </tr>
            </thead>
            <tbody>
              {stateData.map((row, index) => (
                <tr key={index}>
                  <td>{row.State}</td>
                  <td>{fmt(row.opened)}</td>
                  <td>{fmt(row.ds)}</td>
                  <td>{fmt(row.br)}</td>
                  <td>{fmtINR(row.bv)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )

      ) : (

        /* ── CHARTS ── */
        <div className="charts-container">
          <div className="chart-box">
            <h3>Applications by State</h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={processedData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="State" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="opened" fill="#0088FE" name="Opened" />
                <Bar dataKey="ds"     fill="#00C49F" name="Docket Submitted" />
                <Bar dataKey="br"     fill="#FFBB28" name="Benefits Received" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-box">
            <h3>Application Flow Comparison</h3>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={processedData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="State" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="opened" stroke="#0088FE" />
                <Line type="monotone" dataKey="ds"     stroke="#00C49F" />
                <Line type="monotone" dataKey="br"     stroke="#FF8042" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-box">
            <h3>Benefit Value Distribution</h3>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="bv"
                  nameKey="State"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  innerRadius={60}
                  label
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── EXPORT ── */}
      <button onClick={exportToExcel} className="export-btn">
        Export to Excel
      </button>

    </div>
  );
};

export default Table;